import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  type CurrentHitter,
  setCurrentHitter,
  setGameState,
} from "../../../store/slices/game";
import {
  setFocusedEventPayload,
  setTrackingState,
  updateQueueEvent,
} from "../../../store/slices/socket";
import { selectFocusedEvent } from "../../../store/slices/socket/selectors";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import type { IPlayer } from "../../../store/types";
import { CountChanger } from "./CountChanger";
import { InningChanger } from "./InningChanger";
import { ScoreChanger } from "./ScoreChanger";

export const EditGameStateModal = () => {
  const dispatch = useDispatch();
  const { state } = useSelector((state: RootState) => state.view.modalState);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const rosters = useSelector((state: RootState) => state.game.rosters);
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);
  const [newGameState, setNewGameState] = useState(gameState);

  useEffect(() => {
    setNewGameState(gameState);
  }, [gameState]);

  const handleClose = () => {
    dispatch(setModalState({ state: null, data: {} }));
    dispatch(setTrackingState(true));
  };

  const handleSubmit = () => {
    if (isViewingPreviousEvent) {
      dispatch(
        updateQueueEvent({
          ...focusedEvent,
          gameState: newGameState,
        }),
      );
      dispatch(
        setFocusedEventPayload({ ...focusedEvent, gameState: newGameState }),
      );
      dispatch(setModalState({ state: null, data: {} }));
    } else {
      const inningHalfChanged =
        gameState.inning.half !== newGameState.inning.half;

      dispatch(setGameState(newGameState));

      // When inning half changes, update hitter to match new batting team
      if (inningHalfChanged) {
        const oldBattingTeam =
          gameState.inning.half === "top" ? "away" : "home";
        const newBattingTeam =
          newGameState.inning.half === "top" ? "away" : "home";

        const newHitter = (
          rosters[newBattingTeam].currentLineup as (IPlayer & {
            position: number;
          })[]
        ).find((player) => player.position === 1);

        dispatch(
          setCurrentHitter({
            team: oldBattingTeam,
            player: {} as CurrentHitter,
          }),
        );

        if (newHitter?.uuid) {
          dispatch(
            setCurrentHitter({
              team: newBattingTeam,
              player: newHitter as CurrentHitter,
            }),
          );
        }
      }

      dispatch(setModalState({ state: null, data: {} }));
    }
  };

  return (
    <Dialog
      open={state === "Game State Editor"}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="lg"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Edit Game State</DialogTitle>
            </DialogHeader>

            <InningChanger
              newGameState={newGameState}
              setNewGameState={setNewGameState}
            />

            <h3 className="mt-4 font-semibold text-sm uppercase tracking-wide text-gray-400">
              Score
            </h3>
            <ScoreChanger
              newGameState={newGameState}
              setNewGameState={setNewGameState}
            />

            <h3 className="mt-4 font-semibold text-sm uppercase tracking-wide text-gray-400">
              Count
            </h3>
            <CountChanger
              newGameState={newGameState}
              setNewGameState={setNewGameState}
            />
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button variant="secondary-flat-light" onClick={handleSubmit}>
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
